import React, { useState } from 'react';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { ExternalLink } from '@app/components';
import { paths } from '@app/constants';
import { Namespace } from '@data/utils/enums';
import { AntdCol, AntdRow, AntdSkeleton, AntdSpace, AntdTypography, Button } from '@ui/components';
import { ArrowUpRightIcon } from '@ui/icons';
import { TrialLocations } from './components';
import { useTrialDetails } from './hooks';
import './styles.css';

const { Text, Title } = AntdTypography;

const MAX_DISEASES_TO_SHOW = 3;
const TrialDetailsPage = () => {
  const { t } = useTranslation([Namespace.ADMIN]);
  const { state } = useLocation();
  const [showAllDiseases, setShowAllDiseases] = useState(false);

  const { trial, trialId, trialLocations, isTrialLocationsLoading } = useTrialDetails();

  const trialState = state?.trial;
  const previousSearch = state?.from?.search || '';

  const trialToRender = { ...trialState, ...trial };

  const showLoader = !trialToRender;

  return (
    <section id="trial-page" className="p-6">
      <AntdSpace className="justify-between w-full" align="center">
        <Link to={`/${paths.app}/${paths.trials_library}${previousSearch}`}>
          <Button
            icon={<ArrowLeftOutlined />}
            type="link"
            className="text-base-500 hover:text-base-700 px-0"
          >
            {t('trial_details_page.back_link')}
          </Button>
        </Link>
        <ExternalLink
          href={`/${paths.app}/${paths.trial_recruitment}/${paths.trials}/${trialId}/${paths.volunteers}?fromTrialLibrary=true`}
          isRouterLink
        >
          <Button icon={<ArrowUpRightIcon />} type="primary" iconPosition="end">
            {t('trial_details_page.open_in_my_trials')}
          </Button>
        </ExternalLink>
      </AntdSpace>

      {showLoader ? (
        <AntdSkeleton.Input className="block my-4 w-full h-16" />
      ) : (
        <Title className="font-normal text-2xl my-4">
          {trialToRender?.trialId}: {trialToRender?.officialTitle}
        </Title>
      )}

      {showLoader ? (
        <AntdSkeleton.Input className="block my-4 w-1/2 h-8" />
      ) : (
        <Title className="font-normal text-xl my-4 text-base-600" level={2}>
          {trialToRender?.briefTitle}
        </Title>
      )}

      <AntdCol lg={24} xl={16}>
        <AntdRow gutter={[16, 16]}>
          <AntdCol sm={24} md={12} className="flex flex-col">
            <Text className="text-base-500">{t('trial_details_page.disease')}</Text>
            {!trial ? (
              <AntdSkeleton.Input className="block h-6 w-full" />
            ) : (
              <>
                <Text>
                  {showAllDiseases || trialToRender?.conditions?.length <= MAX_DISEASES_TO_SHOW
                    ? trialToRender?.conditions?.map((condition: string) => condition).join(', ')
                    : `${trialToRender?.conditions?.slice(0, MAX_DISEASES_TO_SHOW).join(', ')}...`}
                </Text>
                {trialToRender?.conditions?.length > MAX_DISEASES_TO_SHOW && (
                  <button
                    className="text-start pl-0 border-none bg-transparent cursor-pointer text-primary-600 hover:text-primary-700"
                    onClick={() => setShowAllDiseases(!showAllDiseases)}
                  >
                    {showAllDiseases
                      ? t('trial_details_page.show_less')
                      : t('trial_details_page.show_more', {
                          count: (trialToRender?.conditions?.length ?? 0) - MAX_DISEASES_TO_SHOW,
                        })}
                  </button>
                )}
              </>
            )}
          </AntdCol>
          <AntdCol sm={24} md={12} className="flex flex-col">
            <Text className="text-base-500">{t('trial_details_page.sponsor')}</Text>
            {showLoader ? (
              <AntdSkeleton.Input className="block h-6" />
            ) : (
              <Text>{trialToRender?.leadSponsor}</Text>
            )}
          </AntdCol>
        </AntdRow>
        <AntdRow className="mt-4">
          <AntdCol span={24} className="flex flex-col">
            <Text className="text-base-500">{t('trial_details_page.about_the_study')}</Text>
            {!trial ? (
              <AntdSkeleton.Input className="block h-16 w-full" />
            ) : (
              <Text>{trialToRender?.briefSummary}</Text>
            )}
          </AntdCol>
        </AntdRow>
        <TrialLocations isLoading={isTrialLocationsLoading} locations={trialLocations?.items} />
        <AntdRow className="mt-4">
          <AntdCol span={24} className="flex flex-col">
            <Text className="text-base-500">{t('trial_details_page.treatment_info')}</Text>
            {!trial ? (
              <AntdSkeleton.Input className="block h-28 w-full" />
            ) : (
              <Text>{trialToRender?.detailedDescription || '-'}</Text>
            )}
          </AntdCol>
        </AntdRow>
      </AntdCol>
    </section>
  );
};

export default TrialDetailsPage;
